import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { signup } from '../../../app/store/slices/authSlice';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import logo from '../../../assets/images/logos/576_Cash Canvas-03.png';
import { toast } from 'react-toastify';

export const SignUp = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status } = useSelector((state) => state.auth);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  
    if (name === 'password') {
      const strength = checkPasswordStrength(value);
      setPasswordStrength(strength);
    }
  };
  
  const checkPasswordStrength = (password) => {
    // Individual checks
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[\W_]/.test(password);
    const hasMinLength = password.length >= 8;
  
    // Strong password requires all conditions
    const isStrong = hasUpperCase && hasNumber && hasSpecialChar && hasMinLength;
    
    // Medium password requires at least 2 conditions and minimum length of 6
    const mediumConditions = [hasUpperCase, hasNumber, hasSpecialChar].filter(Boolean).length >= 2;
    const isMedium = mediumConditions && password.length >= 6;
  
    if (isStrong) return 'Strong';
    if (isMedium) return 'Medium';
    return 'Weak';
  };
  

  const handleTogglePassword = () => {
    setShowPassword((v) => !v);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback({ type: '', message: '' });

    if (formData.password.length < 6) {
      setFeedback({ type: 'error', message: 'Password must be at least 6 characters long' });
      return;
    }

    try {
      const result = await dispatch(signup(formData)).unwrap();

      if (result.status === 201) {
        toast.success('Registration successful! Check your email for OTP.');
        navigate('/verify-email', {
          state: {
            email: formData.email,
            message: 'Registration successful! Check your email for OTP.',
          },
        });
      } else if (result.status === 200) {
        toast.success('OTP has been resent to your email address.');
        navigate('/verify-email', {
          state: {
            email: formData.email,
            message: 'OTP has been resent to your email address.',
          },
        });
      }
    } catch (err) {
      if (err?.status === 409) {
        toast.error('Email already registered. Please login.');
        navigate('/login', {
          state: {
            email: formData.email,
            message: 'Email already registered. Please login.',
          },
        });
      } else if (err.code === 'ECONNABORTED') {
        toast.error('Request timeout. Please try again.');
        console.warn('Request timeout');
      } else {
        toast.error('Registration failed. Please try again.');
        console.error('Registration failed:', err.message || 'Unknown error');
      }
    }
  };

  // Add new function to check if form is valid
  const isFormValid = () => {
    const isEmailValid = formData.email && formData.email.includes('@');
    const hasUpperCase = /[A-Z]/.test(formData.password);
    const hasNumber = /\d/.test(formData.password);
    const hasSpecialChar = /[\W_]/.test(formData.password);
    const hasMinLength = formData.password.length >= 8;
    
    const isPasswordValid = hasUpperCase && hasNumber && hasSpecialChar && hasMinLength;
    
    return isEmailValid && isPasswordValid;
  };

  return (
    <div className="flex-1 min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="auth-ui-card">
        <div className="flex justify-center mb-4">
          <img src={logo} alt="Cash Canvas Logo" className="h-56 w-auto" />
        </div>

        <h2 className="txt-page-heading">Sign Up</h2>

        {feedback.message && (
          <div
            className={`mb-4 p-2 rounded text-sm ${
              feedback.type === 'error'
                ? 'bg-red-100 text-red-700'
                : 'bg-green-100 text-green-700'
            }`}
          >
            {feedback.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            required
            className="input-field"
          />

          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder="Create password"
              value={formData.password}
              onChange={handleChange}
              required
              className="input-field"
            />
            <button
              type="button"
              onClick={handleTogglePassword}
              className="absolute inset-y-0 right-3 flex items-center text-gray-500"
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {formData.password && (
            <div className="text-sm mt-2 mb-4">
              {passwordStrength !== 'Strong' && (
                <div className="space-y-1.5">
                  <p className="text-gray-600">Password must contain:</p>
                  <div className="flex flex-wrap items-center gap-x-2 text-sm">
                    <span className={!(/[A-Z]/.test(formData.password)) ? 'text-red-500' : 'text-green-500'}>
                      {!(/[A-Z]/.test(formData.password)) ? '✗' : '✓'} One Uppercase Letter
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className={!(/\d/.test(formData.password)) ? 'text-red-500' : 'text-green-500'}>
                      {!(/\d/.test(formData.password)) ? '✗' : '✓'} One Number
                    </span>
                    <span className="text-gray-400"></span>
                    <span className={!(/[\W_]/.test(formData.password)) ? 'text-red-500' : 'text-green-500'}>
                      {!(/[\W_]/.test(formData.password)) ? '✗' : '✓'} One Special Character
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className={formData.password.length < 8 ? 'text-red-500' : 'text-green-500'}>
                      {formData.password.length < 8 ? '✗' : '✓'} Minimum 8+ Characters
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'loading' || !isFormValid()}
            className="submit-btn"
          >
            {status === 'loading' ? 'Creating account...' : 'Continue'}
          </button>
        </form>

        <p className="text-xs mt-4 text-gray-600">
          By signing up, you agree to Cash Canvas {' '}
          <Link to="/terms" className="text-blue-800 hover:text-btn-primary-hover">
            Terms
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="text-blue-800 hover:text-btn-primary-hover">
            Privacy Policy
          </Link>.
        </p>

        <p className="mt-4 text-sm text-gray-600 text-center">
          Already have an account?{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-blue-800 hover:text-btn-primary-hover underline"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};